import axios from "axios";
import {jwtDecode} from "jwt-decode";

//import.meta.env.VITE_API_URL
export default class ApiHelper {
    _urlBase = '';
    _cookie = null;


    constructor() {
        this._urlBase = ApiHelper.getUrlBase();
    }


    static getUrlBase() {
        return (typeof process !== 'undefined' && process?.env?.API_ORIGIN)
                || (import.meta.env.VITE_API_URL)
                || '';
    }


    /**
     * Build full URL with parameters
     * @param {string} endpoint a relative or absolute API endpoint URL
     * @param {Object} params key-value parameters to append to URL
     * @returns {string} full URL
     * @private
     */
    _buildUrl(endpoint, params = {}) {
        let base = (endpoint.indexOf('https://') !== -1) ? (endpoint) : (this._urlBase + endpoint);
        const url = new URL(base);

        Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));

        return url.toString();
    }


    /**
     * Make GET request to API endpoint
     * @param {string} endpoint a relative API endpoint URL
     * @param {Object} params key-value parameters to append to URL
     * @returns {Promise|null}
     */
    async getRequest(endpoint, params = {}) {
        let fullUrl = this._buildUrl(endpoint, params),
            requestParams = {
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain, */*'
                }
            };

        return this._request(requestParams);
    }


    /**
     * Make POST request to API endpoint
     * @param {string} endpoint a relative API endpoint URL
     * @param {Object|null} data data to send
     * @param {boolean} sendRaw whether to send raw data
     * @param {Object} extraParams additional axios request params (e.g. onUploadProgress)
     *
     * @returns {Promise|null}
     */
    async postRequest(endpoint, data = null, sendRaw = false, extraParams = {}) {
        let fullUrl = this._buildUrl(endpoint),
            requestParams = {
                ...extraParams,
                method: 'POST',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': sendRaw ? 'multipart/form-data; charset=UTF-8' : 'application/json; charset=UTF-8'
                },
                data: sendRaw ? data : JSON.stringify(data),
            };

        return this._request(requestParams);
    }


    /**
     * Make PUT request to API endpoint
     * @param {string} endpoint a relative API endpoint URL
     * @param {Object|null} data data to send
     *
     * @returns {Promise|null}
     */
    async putRequest(endpoint, data = null) {
        let fullUrl = this._buildUrl(endpoint),
            requestParams = {
                method: 'PUT',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                data: JSON.stringify(data),
            };

        return this._request(requestParams);
    }


    /**
     * Make PATCH request to API endpoint
     * @param {string} endpoint a relative API endpoint URL
     * @param {Object|null} data data to send
     *
     * @returns {Promise|null}
     */
    async patchRequest(endpoint, data = null) {
        let fullUrl = this._buildUrl(endpoint),
            requestParams = {
                method: 'PATCH',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                data: JSON.stringify(data),
            };

        return this._request(requestParams);
    }


    /**
     * Make DELETE request to API endpoint
     * @param {string} endpoint a relative API endpoint URL
     *
     * @returns {Promise|null}
     */
    async deleteRequest(endpoint) {
        let fullUrl = this._buildUrl(endpoint),
            requestParams = {
                method: 'DELETE',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                }
            };

        return this._request(requestParams);
    }


    setCookie(cookie) {
        this._cookie = cookie;
    }


    /**
     * Perform a request and handle errors, if any
     * @param {Object} requestParams
     * @returns {Promise<*>}
     * @private
     */
    async _request(requestParams) {
        requestParams.headers ??= {};
        requestParams.withCredentials ??= true;

        if (this._cookie) {
            requestParams.headers['Cookie'] = this._cookie;
        }

        return axios.request(requestParams);
    }
}